import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { serializeColorFunc } from './useFractalWorker';
import { paletteColor } from './fractalPalette';
import { appendHistory, captureHistoryImage } from './fractalHistory';
import { cameraFromRanges, numberCameraFromSnapshot } from './fractalCamera';
import { MAX_ITERATIONS as SHALLOW_MAX_ITERATIONS } from './fractalIteration';
import { loadDeepZoom } from './loadDeepZoom';
import { useFractalRenderer } from './useFractalRenderer';
import { FullscreenButton, useFullscreenControl } from './fullscreenControl';
import { cappedBackingSize } from './ResponsiveFractalCanvas';

const DEEP_ITERATIONS = 4096;
const WHEEL_ZOOM_RATE = 0.002;
const MAX_WHEEL_ZOOM_STEP = 0.18;
const TRACKPAD_PINCH_ZOOM_RATE = 0.008;
const MAX_TRACKPAD_PINCH_ZOOM_STEP = 0.6;
const progressSuffix = value => Number.isFinite(value)
  ? ` ${Math.round(Math.max(0, Math.min(1, value)) * 100)}%` : '';
function deepStatusText(status, width, height) {
  if (!status) return null;
  const { phase, progress } = status;
  const refiningFullDetail = Boolean(
    status.hasPixels && (
      status.displayReady ||
      status.completedResolution >= 320
    )
  );
  if (phase === 'reference') {
    if (refiningFullDetail) return null;
    return progress?.phase === 'validating' ? 'Checking view…' : 'Calculating view…';
  }
  if (phase === 'rendering' || phase === 'preview') {
    if (phase === 'preview' && status.completedResolution >= 320) return null;
    const preview = status.width < width || status.height < height ||
      Boolean(status.hasPixels && !refiningFullDetail);
    return preview ? `Building preview…${progressSuffix(progress)}` : null;
  }
  if (phase === 'repairing') return refiningFullDetail ? null : 'Recovering detail…';
  if (phase === 'complete') return null;
  return status.message || (phase === 'loading' ? 'Loading deep zoom…' : phase === 'limited' ? 'More detail is available.'
    : phase === 'error' ? 'Deep zoom could not be rendered.' : null);
}

function parseScientificParts(str) {
  if (typeof str !== 'string') str = String(str ?? '');
  const match = str.trim().match(/^([+-]?)(\d*)(?:\.(\d*))?(?:e([+-]?\d+))?$/i);
  if (!match) return null;
  const sign = match[1] === '-' ? -1 : 1;
  const intPart = match[2] || '';
  const fracPart = match[3] || '';
  const rawExp = Number(match[4] ?? 0);
  if (!intPart && !fracPart) return null;
  const combined = intPart + fracPart;
  const firstNonZero = combined.search(/[1-9]/);
  if (firstNonZero === -1) return { sign: 0, mantissa: 0, exponent: 0 };
  const exponent = rawExp + intPart.length - firstNonZero - 1;
  const sigDigits = combined.slice(firstNonZero, firstNonZero + 16);
  const mantissa = Number(`${sigDigits[0]}.${sigDigits.slice(1) || '0'}`);
  return { sign, mantissa, exponent };
}

function formatSpanX(spanY, aspect = 1, scaleFactor = 1) {
  const safeAspect = (Number.isFinite(aspect) && aspect > 0 ? aspect : 1) *
    (Number.isFinite(scaleFactor) && scaleFactor > 0 ? scaleFactor : 1);
  const num = Number(spanY) * safeAspect;
  if (Number.isFinite(num) && num > 0) {
    return `span ${num.toExponential(2)}`;
  }
  const parts = parseScientificParts(spanY);
  if (!parts || parts.sign <= 0 || !(parts.mantissa > 0)) return 'span 0.00e+0';
  const scaledMantissa = parts.mantissa * safeAspect;
  const extraExp = Math.floor(Math.log10(scaledMantissa));
  let norm = scaledMantissa / (10 ** extraExp);
  let exp = parts.exponent + extraExp;
  let fixed = norm.toFixed(2);
  if (Number(fixed) >= 10) {
    norm /= 10;
    exp += 1;
    fixed = norm.toFixed(2);
  }
  return `span ${fixed}e${exp >= 0 ? `+${exp}` : exp}`;
}

function formatCoordinateSigFigs(valueStr, sigFigs = 3) {
  const num = Number(valueStr);
  if (Number.isFinite(num) && num !== 0) {
    return num.toPrecision(sigFigs);
  }
  const parts = parseScientificParts(valueStr);
  if (!parts || parts.sign === 0) return (0).toPrecision(sigFigs);
  const signPrefix = parts.sign < 0 ? '-' : '';
  let norm = parts.mantissa;
  let exp = parts.exponent;
  let fixed = norm.toFixed(sigFigs - 1);
  if (Number(fixed) >= 10) {
    norm /= 10;
    exp += 1;
    fixed = norm.toFixed(sigFigs - 1);
  }
  return `${signPrefix}${fixed}e${exp >= 0 ? `+${exp}` : exp}`;
}

function describeCameraSnapshot(snapshot, pendingOperations = [], includeCenter = false) {
  if (!snapshot) return 'Deep zoom';
  let aspect = snapshot.aspect || 1;
  let zoomScale = 1;
  let centerShiftX = 0;
  let centerShiftY = 0;
  for (const op of pendingOperations) {
    if (op.type === 'zoom') {
      const [factor, u = 0.5, v = 0.5] = op.args;
      centerShiftX += (u - 0.5) * zoomScale * aspect * (1 - factor);
      centerShiftY += (v - 0.5) * zoomScale * (1 - factor);
      zoomScale *= factor;
    } else if (op.type === 'aspect') {
      aspect = op.args[0] || aspect;
    } else if (op.type === 'pan') {
      const [dx, dy, w, h] = op.args;
      if (w > 0 && h > 0) {
        centerShiftX += (dx / w) * zoomScale * aspect;
        centerShiftY += (dy / h) * zoomScale;
      }
    }
  }
  const spanLabel = formatSpanX(snapshot.spanY, aspect, zoomScale);
  if (!includeCenter) return spanLabel;
  const spanYNum = Number(snapshot.spanY);
  const cxNum = Number(snapshot.centerX);
  const cyNum = Number(snapshot.centerY);
  const cxStr = Number.isFinite(cxNum) && Number.isFinite(spanYNum)
    ? String(cxNum + centerShiftX * spanYNum)
    : snapshot.centerX;
  const cyStr = Number.isFinite(cyNum) && Number.isFinite(spanYNum)
    ? String(cyNum + centerShiftY * spanYNum)
    : snapshot.centerY;
  return `${spanLabel} · center (${formatCoordinateSigFigs(cxStr)}, ${formatCoordinateSigFigs(cyStr)})`;
}

const fittedAspect = (policy, width, height) => width === policy.width && height === policy.height
  ? policy.aspect : policy.aspect * (width / policy.width) / (height / policy.height);
const fittedSpanScale = (policy, width, height) => {
  const aspect = fittedAspect(policy, width, height);
  return aspect > 0 && aspect < policy.aspect ? policy.aspect / aspect : 1;
};
const entrySpanScale = (policy, entry) => {
  const aspectOp = entry?.pendingOperations?.filter(op => op.type === 'aspect').at(-1);
  const aspect = aspectOp?.args?.[0] ?? entry?.camera?.aspect ?? policy.aspect;
  return aspect > 0 && aspect < policy.aspect ? policy.aspect / aspect : 1;
};
function inverseViewTransform(camera, source) {
  const { scale, offset } = camera.viewTransform(source);
  const result = { scaleX: 1 / scale[0], scaleY: 1 / scale[1], x: -offset[0] / scale[0], y: -offset[1] / scale[1] };
  return Object.values(result).every(Number.isFinite) ? result : null;
}

function transformPreview(canvas, transform) {
  if (!canvas || !transform) return;
  canvas.style.transformOrigin = '0 0';
  canvas.style.transform = `translate(${transform.x * 100}%, ${transform.y * 100}%) scale(${transform.scaleX}, ${transform.scaleY})`;
}
function forwardSnapshotTransform(target, source) {
  const sy = Number(target.spanY) / Number(source.spanY);
  const sx = sy * target.aspect / source.aspect;
  const transform = { scale: [sx, sy], offset: [
    0.5 + (Number(target.centerX) - Number(source.centerX)) / (Number(source.spanY) * source.aspect) - sx / 2,
    0.5 + (Number(target.centerY) - Number(source.centerY)) / Number(source.spanY) - sy / 2
  ], aspect: target.aspect };
  return [...transform.scale, ...transform.offset].every(Number.isFinite) ? transform : null;
}
function advanceForwardTransform(transform, operation) {
  if (operation.type === 'zoom') {
    const [factor, u, v] = operation.args;
    transform.offset[0] += transform.scale[0] * (1 - factor) * u;
    transform.offset[1] += transform.scale[1] * (1 - factor) * v;
    transform.scale[0] *= factor;
    transform.scale[1] *= factor;
  } else if (operation.type === 'aspect') {
    const factor = operation.args[0] / transform.aspect;
    transform.offset[0] += transform.scale[0] * (1 - factor) / 2;
    transform.scale[0] *= factor;
    transform.aspect = operation.args[0];
  } else {
    const [dx, dy, width, height] = operation.args;
    transform.offset[0] += transform.scale[0] * dx / width;
    transform.offset[1] += transform.scale[1] * dy / height;
  }
}
const applyOperation = (camera, operation, width, height, iterationBudget = DEEP_ITERATIONS) => operation.type === 'zoom'
  ? camera.zoomAt(...operation.args, width, height, iterationBudget)
  : operation.type === 'aspect' ? camera.fitAspect(...operation.args, width, height, iterationBudget)
    : camera.panPixels(...operation.args, width, height, iterationBudget);

function replayOperations(camera, operations, width, height, iterationBudget = DEEP_ITERATIONS) {
  let limitError = null;
  for (const operation of operations) {
    try { camera = applyOperation(camera, operation, width, height, iterationBudget); }
    catch (error) {
      if (error.code !== 'CAMERA_PRECISION_LIMIT') throw error;
      // A rejected zoom is atomic. Later queued gestures may zoom out or pan
      // within the available precision and must still be applied in order.
      limitError = error;
    }
  }
  return { camera, limitError };
}

function measureFullscreenDimensions(canvas, container, fallbackWidth, fallbackHeight) {
  const canvasRect = !canvas?.style?.transform ? canvas?.getBoundingClientRect?.() : null;
  const containerRect = container?.getBoundingClientRect?.();
  const cssWidth = (canvasRect?.width > 0 ? canvasRect.width : 0)
    || (containerRect?.width > 0 ? containerRect.width : 0)
    || container?.clientWidth
    || (typeof window !== 'undefined' ? window.innerWidth : 0)
    || fallbackWidth;
  const cssHeight = (canvasRect?.height > 0 ? canvasRect.height : 0)
    || (containerRect?.height > 0 ? containerRect.height : 0)
    || container?.clientHeight
    || (typeof window !== 'undefined' ? window.innerHeight : 0)
    || fallbackHeight;
  return cappedBackingSize(cssWidth, cssHeight) || { width: fallbackWidth, height: fallbackHeight };
}

export function serializeFractalPermalink(snapshot, juliaC = null, baseHref = 'https://robotmoon.com/julia-set/') {
  if (!snapshot) return baseHref;
  const params = new URLSearchParams();
  if (snapshot.centerX !== undefined) params.set('cx', String(snapshot.centerX));
  if (snapshot.centerY !== undefined) params.set('cy', String(snapshot.centerY));
  if (snapshot.spanY !== undefined) params.set('span', String(snapshot.spanY));
  if (Array.isArray(juliaC) && juliaC.length >= 2) {
    params.set('jc', `${juliaC[0]},${juliaC[1]}`);
  }
  const cleanBase = String(baseHref).split('#')[0];
  return `${cleanBase}#${params.toString()}`;
}

export function parseFractalPermalink(hashOrSearch = '') {
  const raw = String(hashOrSearch).replace(/^[#?]/, '');
  if (!raw) return null;
  const params = new URLSearchParams(raw);
  const centerX = params.get('cx');
  const centerY = params.get('cy');
  const spanY = params.get('span');
  if (!centerX || !centerY || !spanY) return null;
  if (!Number.isFinite(Number(centerX)) || !Number.isFinite(Number(centerY))) return null;
  let juliaC = null;
  const jc = params.get('jc');
  if (jc) {
    const parts = jc.split(',').map(Number);
    if (parts.length === 2 && parts.every(Number.isFinite)) juliaC = parts;
  }
  return { centerX, centerY, spanY, juliaC };
}

export function formatMagnification(spanY, baseSpan = 3.2) {
  const str = String(spanY ?? '').trim();
  if (!str) return '1.0\u00d7';
  const num = Number(str);
  if (Number.isFinite(num) && num > 1e-6) {
    const mag = baseSpan / num;
    if (mag < 10) return `${mag.toFixed(1)}\u00d7`;
    if (mag < 1000) return `${Math.round(mag)}\u00d7`;
    return `${mag.toExponential(1)}\u00d7`;
  }
  const expMatch = /[eE]([+-]?\d+)$/.exec(str);
  if (expMatch) {
    const exp = -Number(expMatch[1]);
    return `10^${exp}\u00d7`;
  }
  return `${baseSpan}\u00d7`;
}

/** All input is expressed in screen coordinates; the camera owns world arithmetic. */
export function FractalCanvas({ width: propWidth, height: propHeight,
  presetWidth, presetHeight, xRange: initialX, yRange: initialY,
  camera: initialSnapshot, iterationBudget: propIterationBudget, juliaC, colorFunc: customColorFunc, palette, preferredRenderer = 'webgl',
  cpuReusePaletteArray = true, cpuExactPanReuse = true,
  cpuInteriorDispatch = true, onCpuFrameReady,
  isFullscreen: controlledFullscreen, onFullscreenChange,
  showFullscreenButton = true, showControls = false,
  fallbackSrc, fallbackAlt, fetchPriority, fetchpriority }) {
  const {
    containerRef,
    isFullscreen,
    isFullscreenRef,
    toggleFullscreen,
  } = useFullscreenControl({ isFullscreen: controlledFullscreen, onFullscreenChange });
  const resolvedFetchPriority = fetchPriority ?? fetchpriority;
  const fallbackImgRef = useRef(null);
  const [fullscreenSize, setFullscreenSize] = useState(null);
  const deepIterations = Number.isInteger(propIterationBudget) && propIterationBudget > 0
    ? propIterationBudget
    : DEEP_ITERATIONS;
  const canvasRef = useRef(null);
  const resolvedFullscreenSize = isFullscreen
    ? (fullscreenSize || measureFullscreenDimensions(canvasRef.current, containerRef.current, propWidth, propHeight))
    : null;
  const width = isFullscreen && resolvedFullscreenSize ? resolvedFullscreenSize.width : propWidth;
  const height = isFullscreen && resolvedFullscreenSize ? resolvedFullscreenSize.height : propHeight;
  const imgWidth = presetWidth || propWidth;
  const imgHeight = presetHeight || propHeight;
  const initialCamera = useRef(undefined);
  if (initialCamera.current === undefined) initialCamera.current = initialSnapshot ? null : cameraFromRanges(initialX, initialY);
  // Legacy callers may deliberately supply a world aspect unlike the backing
  // buffer. Retain that relationship when the output dimensions change.
  const aspectPolicyRef = useRef({ aspect: initialSnapshot?.aspect || initialCamera.current.aspect, width: propWidth, height: propHeight });
  const spanScaleBaselineRef = useRef(null);
  const cameraRef = useRef(initialCamera.current);
  const deepCanvasRef = useRef(null);
  const runtimeRef = useRef(null);
  const prewarmRef = useRef(null);
  const controllerRef = useRef(null);
  const controllerCanvasRef = useRef(null);
  const handoffRef = useRef(initialSnapshot ? { snapshot: initialSnapshot, operations: [] } : null);
  const loadGenerationRef = useRef(0);
  const mountedRef = useRef(false);
  const deepRef = useRef(!!initialSnapshot);
  const deepImageVisibleRef = useRef(false);
  const shallowPreviewVisibleRef = useRef(false);
  const fallbackBackdropVisibleRef = useRef(Boolean(fallbackSrc && initialSnapshot));
  const shallowBackingRef = useRef({ width, height });
  const backingFrozenRef = useRef(false);
  const refreshShallowAfterUnfreezeRef = useRef(false);
  const [deep, setDeep] = useState(false);
  const [status, setStatus] = useState(null);
  const statusRef = useRef(null);
  // Native wheel and keyboard handlers may run before React commits another
  // render. History transactions therefore share synchronous camera lifetime.
  const historyRef = useRef([]);
  const redoRef = useRef([]);
  const [historyLength, setHistoryLength] = useState(0);
  const [viewText, setViewText] = useState(() => {
    const snap = initialSnapshot || initialCamera.current?.snapshot();
    return {
      inline: describeCameraSnapshot(snap, [], false),
      fullscreen: describeCameraSnapshot(snap, [], true),
    };
  });
  const syncCameraUi = useCallback(() => {
    const nextHistoryLength = historyRef.current.length;
    setHistoryLength(prev => (prev === nextHistoryLength ? prev : nextHistoryLength));
    const handoff = handoffRef.current;
    const snap = handoff ? handoff.snapshot : cameraRef.current?.snapshot();
    const ops = handoff ? handoff.operations : [];
    const inline = describeCameraSnapshot(snap, ops, false);
    const fullscreen = describeCameraSnapshot(snap, ops, true);
    setViewText(prev => (
      prev.inline === inline && prev.fullscreen === fullscreen
        ? prev
        : { inline, fullscreen }
    ));
  }, []);
  const [shiftPressed, setShiftPressed] = useState(false);
  const frameRef = useRef(null);
  const frameOptionsRef = useRef(null);
  const timerRef = useRef(null);
  const wheelHistoryRef = useRef(false);
  const needsRefinementRef = useRef(false);
  const zoomAnchorRef = useRef(null);
  const panRef = useRef(null);
  const pinchRef = useRef(null);
  const boxSelectRef = useRef(null);
  const [boxSelect, setBoxSelect] = useState(null);
  const presentRef = useRef(null);
  const queuePresentRef = useRef(null);
  const loadRef = useRef(null);
  const propsRef = useRef({ width, height });
  propsRef.current = { width, height };
  const effectivePalette = useMemo(
    () => (palette && juliaC ? { ...palette, juliaC } : palette),
    [palette, juliaC]
  );
  const colorFunc = useMemo(() => {
    if (effectivePalette) {
      const fn = value => paletteColor(value, effectivePalette);
      if (effectivePalette.juliaC) fn.juliaC = effectivePalette.juliaC;
      if (effectivePalette.demStrength > 0) fn.demStrength = effectivePalette.demStrength;
      return fn;
    }
    if (customColorFunc && juliaC) {
      const fn = value => customColorFunc(value);
      fn.juliaC = juliaC;
      return fn;
    }
    return customColorFunc;
  }, [effectivePalette, customColorFunc, juliaC]);
  const colorParams = useMemo(() => {
    const base = effectivePalette || serializeColorFunc(colorFunc);
    return juliaC && !base?.juliaC ? { ...base, juliaC } : base;
  }, [effectivePalette, colorFunc, juliaC]);
  const colorParamsRef = useRef(colorParams);
  colorParamsRef.current = colorParams;
  const imagePaletteKey = JSON.stringify(colorParams);
  const imageIdentityRef = useRef(null);
  imageIdentityRef.current = { paletteKey: imagePaletteKey, colorFunc: effectivePalette ? null : customColorFunc };
  const onFallback = useCallback(() => {
    const ranges = !deepRef.current && !handoffRef.current
      ? cameraRef.current?.toShallowRanges() : null;
    // A failed pan shader has already committed its requested camera. Ending
    // the gesture avoids applying that movement again on the replacement canvas.
    panRef.current = null;
    if (frameRef.current != null) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
    frameOptionsRef.current = null;
    return ranges;
  }, []);
  const { canvasEpoch, recovering, present: presentShallow, reservePresentation: reserveShallowPresentation,
    suspend, restoreImage, canCaptureHistory, getPresentedSnapshot, projectOriginalSource } = useFractalRenderer(canvasRef, {
    initialRanges: initialCamera.current?.toShallowRanges() || null,
    colorFunc, colorParams, paletteParams: effectivePalette, cpuReusePaletteArray, cpuExactPanReuse,
    cpuInteriorDispatch,
    preferredRenderer, onFallback, onCpuFrameReady,
  });
  const juliaKey = JSON.stringify(effectivePalette?.juliaC ?? juliaC ?? null);
  const initialJuliaKeyRef = useRef(undefined);
  if (initialJuliaKeyRef.current === undefined) initialJuliaKeyRef.current = juliaKey;
  const hideFallbackImg = Boolean(
    isFullscreen || deep || juliaKey !== initialJuliaKeyRef.current || (!initialSnapshot && historyLength > 0)
  );
  useEffect(() => {
    if (fallbackImgRef.current) {
      fallbackImgRef.current.style.visibility = hideFallbackImg ? 'hidden' : '';
    }
  }, [hideFallbackImg, canvasEpoch]);
  const report = useCallback(next => {
    if (!mountedRef.current) return;
    statusRef.current = next;
    setStatus(prev => {
      const { width: currentWidth, height: currentHeight } = propsRef.current;
      const prevText = deepStatusText(prev, currentWidth, currentHeight);
      const nextText = deepStatusText(next, currentWidth, currentHeight);
      const prevBudget = prev?.iterationBudget || prev?.progress?.iterationBudget || deepIterations;
      const nextBudget = next?.iterationBudget || next?.progress?.iterationBudget || deepIterations;
      const prevAction = prev?.phase === 'error' || prev?.phase === 'limited';
      const nextAction = next?.phase === 'error' || next?.phase === 'limited';
      if (Boolean(prev) === Boolean(next) && prevText === nextText &&
          prevBudget === nextBudget && prevAction === nextAction) return prev;
      return next;
    });
  }, [deepIterations]);

  const cancelFrame = useCallback(() => {
    if (frameRef.current != null) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
    frameOptionsRef.current = null;
    clearTimeout(timerRef.current);
    timerRef.current = null;
    needsRefinementRef.current = false;
  }, []);

  const pendingSourceProjection = useCallback(handoff => {
    const source = getPresentedSnapshot();
    if (!source) return null;
    const sameSource = handoff.projectionSource && ['centerX', 'centerY', 'spanY', 'aspect'].every(
      key => source[key] === handoff.projectionSource[key]);
    if (!sameSource) {
      handoff.projection = forwardSnapshotTransform(handoff.snapshot, source);
      handoff.projectionSource = source;
      handoff.projectedOperations = 0;
    }
    if (!handoff.projection) return null;
    while (handoff.projectedOperations < handoff.operations.length) {
      advanceForwardTransform(handoff.projection, handoff.operations[handoff.projectedOperations++]);
    }
    return handoff.projection;
  }, [getPresentedSnapshot]);

  const ensureController = useCallback(runtime => {
    const target = deepCanvasRef.current;
    if (controllerRef.current && controllerCanvasRef.current === target) return controllerRef.current;
    controllerRef.current?.dispose();
    controllerRef.current = null;
    controllerCanvasRef.current = target;
    controllerRef.current = runtime.createDeepZoomController(target, {
      performanceOptions: { tileSize: 'auto', completedFrameCache: true,
        completedFrameHistory: 2, referenceCacheCenters: 3,
        nearbyReferenceRecall: true, conjugateReferenceRecall: true,
        analyticInteriorTiles: true, reuseCaptured160Preview: true,
        directInteriorFrame: 'reuse' },
      onStatus: next => {
        if (!deepRef.current) return;
        const hasPixels = next.hasPixels || next.completedResolution > 0 ||
          (next.phase === 'rendering' && next.progress > 0);
        if (!deepImageVisibleRef.current && hasPixels &&
            ((!shallowPreviewVisibleRef.current && !fallbackBackdropVisibleRef.current) || next.displayReady)) {
          deepImageVisibleRef.current = true;
          fallbackBackdropVisibleRef.current = false;
          setDeep(true);
          if (canvasRef.current) {
            canvasRef.current.style.transform = '';
            delete canvasRef.current._fractalSourceProjection;
            if (canvasRef.current._fractalRenderStats?.originalSourceProjection)
              canvasRef.current._fractalRenderStats = { ...canvasRef.current._fractalRenderStats,
                originalSourceProjection: false };
          }
        }
        report(next);
      },
    });
    return controllerRef.current;
  }, [report]);

  const ensureRuntime = useCallback(async (retry = false) => {
    if (runtimeRef.current && !retry) return runtimeRef.current;
    const runtime = await loadDeepZoom({ retry });
    runtimeRef.current = runtime;
    if (mountedRef.current && !controllerRef.current && !prewarmRef.current) {
      const prepare = () => {
        prewarmRef.current = null;
        const canvas = canvasRef.current;
        const rect = canvas?.getBoundingClientRect();
        if (mountedRef.current && !document.hidden && rect?.bottom >= 0 &&
            rect.top <= window.innerHeight && !controllerRef.current) {
          try { ensureController(runtime); } catch (_error) {
            // The handoff reports unsupported contexts if the user reaches it.
            controllerCanvasRef.current = null;
          }
        }
      };
      if (window.requestIdleCallback)
        prewarmRef.current = { idle: window.requestIdleCallback(prepare) };
      else prewarmRef.current = { timer: setTimeout(prepare, 32) };
    }
    return runtime;
  }, [ensureController]);

  const present = useCallback((options = {}) => {
    if (!mountedRef.current) return;
    const camera = cameraRef.current;
    if (handoffRef.current || !camera) {
      suspend({ restoreSource: false });
      const handoff = handoffRef.current;
      if (handoff && canvasRef.current?._useWebGL && !deepImageVisibleRef.current) {
        if (!Number.isFinite(options.presentationTime)) queuePresentRef.current?.(options);
        else {
          const projection = pendingSourceProjection(handoff);
          if (projection) shallowPreviewVisibleRef.current = projectOriginalSource(null, {
            presentationTime: options.presentationTime, forwardTransform: projection,
            pendingCamera: { snapshot: handoff.snapshot,
              operations: handoff.operations.map(operation => ({ ...operation, args: [...operation.args] })) }
          });
        }
      }
      return;
    }
    const { width: renderWidth, height: renderHeight } = propsRef.current;
    const isDeep = camera.needsDeep(renderWidth, renderHeight, deepRef.current);
    if (isDeep) {
      // This RAF owns the one visible composition. Project the original source
      // directly instead of restoring then transforming a mixed Number frame.
      suspend({ restoreSource: false });
      deepRef.current = true;
      if (!deepImageVisibleRef.current) {
        if (canvasRef.current?._useWebGL && !Number.isFinite(options.presentationTime)) {
          // Import completion, resize and release can invalidate synchronously.
          // Their original-source draw joins the same coalesced input RAF.
          queuePresentRef.current?.(options);
        } else {
          const source = getPresentedSnapshot();
          const projected = projectOriginalSource(camera, { presentationTime: options.presentationTime });
          // A failed GPU projection must not label an old mixed composition as
          // the exact source. CSS is the bridge for an ordinary 2D source only.
          const transform = !projected && !canvasRef.current?._useWebGL && source && inverseViewTransform(camera, source);
          shallowPreviewVisibleRef.current = projected || !!transform;
          if (transform) {
            if (fallbackImgRef.current) fallbackImgRef.current.style.visibility = 'hidden';
            transformPreview(canvasRef.current, transform);
          }
        }
      }
      if (!runtimeRef.current) {
        handoffRef.current = { snapshot: camera.snapshot(), operations: [] };
        loadRef.current?.();
        return;
      }
      try {
        ensureController(runtimeRef.current).update(camera, { colorParams: colorParamsRef.current,
          interactive: !!options.interactive, pan: !!options.pan,
          width: renderWidth, height: renderHeight,
          iterationBudget: deepIterations, anchor: options.anchor || zoomAnchorRef.current,
          presentationTime: options.presentationTime });
      } catch (error) {
        report({ phase: 'error', message: error.message, error });
      }
    } else {
      if (deepRef.current) controllerRef.current?.cancel();
      deepRef.current = false;
      deepImageVisibleRef.current = false;
      shallowPreviewVisibleRef.current = false;
      setDeep(false);
      report(null);
      if (canvasRef.current) canvasRef.current.style.transform = '';
      if (deepCanvasRef.current) deepCanvasRef.current.style.transform = '';
      presentShallow(camera.toShallowRanges(), options);
    }
  }, [deepIterations, ensureController, getPresentedSnapshot, pendingSourceProjection,
    presentShallow, projectOriginalSource, report, suspend]);
  presentRef.current = present;

  const startHandoff = useCallback(async (retry = false) => {
    const generation = ++loadGenerationRef.current;
    report({ phase: 'loading', message: 'Loading deep zoom…' });
    try {
      const runtime = await ensureRuntime(retry);
      if (!mountedRef.current || generation !== loadGenerationRef.current) return;
      const handoff = handoffRef.current;
      if (!handoff) return;
      const { width: renderWidth, height: renderHeight } = propsRef.current;
      const base = runtime.decimalCameraFromSnapshot(handoff.snapshot,
        { width: renderWidth, height: renderHeight, iterationBudget: deepIterations });
      const { camera: next, limitError } = replayOperations(base, handoff.operations, renderWidth, renderHeight, deepIterations);
      cameraRef.current = next;
      handoffRef.current = null;
      syncCameraUi();
      if (deepCanvasRef.current) deepCanvasRef.current.style.transform = '';
      presentRef.current({ interactive: needsRefinementRef.current });
      if (limitError) report({ phase: 'limited', message: limitError.message });
    } catch (error) {
      if (mountedRef.current && generation === loadGenerationRef.current) report({ phase: 'error', message: error.message, error });
    }
  }, [deepIterations, ensureRuntime, report, syncCameraUi]);
  loadRef.current = startHandoff;

  const queuePresent = useCallback((options = {}) => {
    needsRefinementRef.current = !!options.interactive;
    if (options.anchor) zoomAnchorRef.current = options.anchor;
    frameOptionsRef.current = options;
    if (frameRef.current != null) return;
    // The input RAF owns this presentation. Cancel an older renderer RAF so
    // it cannot compose the previous camera immediately before this one.
    if (deepRef.current) controllerRef.current?.reservePresentation?.();
    else reserveShallowPresentation();
    frameRef.current = requestAnimationFrame(time => {
      frameRef.current = null;
      const latest = frameOptionsRef.current;
      frameOptionsRef.current = null;
      if (latest) presentRef.current({ ...latest, presentationTime: time });
    });
  }, [reserveShallowPresentation]);
  queuePresentRef.current = queuePresent;

  // During an import, retain the original bitmap and apply only its accumulated
  // screen transform. Exact camera commands are replayed, in order, on promotion.
  const previewPending = useCallback(operation => {
    const handoff = handoffRef.current;
    if (canvasRef.current?._useWebGL && !deepImageVisibleRef.current) {
      shallowPreviewVisibleRef.current = !!pendingSourceProjection(handoff);
      queuePresentRef.current?.({ interactive: true,
        anchor: operation.type === 'zoom' ? operation.args.slice(1, 3) : undefined });
      return;
    }
    let transform = handoff.transform;
    if (!transform) {
      const source = getPresentedSnapshot();
      const current = cameraRef.current;
      const snapshot = current?.snapshot();
      const baseMatches = snapshot && ['centerX', 'centerY', 'spanY', 'aspect'].every(key => snapshot[key] === handoff.snapshot[key]);
      const projected = source && baseMatches ? inverseViewTransform(current, source) : null;
      transform = { ...(projected || { scaleX: 1, scaleY: 1, x: 0, y: 0 }), aspect: handoff.snapshot.aspect };
    }
    if (operation.type === 'zoom') {
      const [factor, u, v] = operation.args;
      transform.scaleX /= factor;
      transform.scaleY /= factor;
      transform.x = (transform.x - u) / factor + u;
      transform.y = (transform.y - v) / factor + v;
    } else if (operation.type === 'aspect') {
      const aspect = operation.args[0];
      const factor = transform.aspect / aspect;
      transform.scaleX *= factor;
      transform.x = (transform.x - 0.5) * factor + 0.5;
      transform.aspect = aspect;
    } else {
      const [dx, dy, cssWidth, cssHeight] = operation.args;
      transform.x -= dx / cssWidth;
      transform.y -= dy / cssHeight;
    }
    handoff.transform = transform;
    const target = deepRef.current && controllerRef.current ? deepCanvasRef.current : canvasRef.current;
    if (target === canvasRef.current) shallowPreviewVisibleRef.current = !!getPresentedSnapshot();
    if (fallbackImgRef.current) fallbackImgRef.current.style.visibility = 'hidden';
    transformPreview(target, transform);
  }, [getPresentedSnapshot, pendingSourceProjection]);

  const operate = useCallback(operation => {
    if (fallbackBackdropVisibleRef.current && operation.type !== 'aspect') {
      fallbackBackdropVisibleRef.current = false;
      if (deepRef.current && !deepImageVisibleRef.current && !shallowPreviewVisibleRef.current) {
        const cur = statusRef.current;
        const hasPixels = cur && (cur.hasPixels || cur.completedResolution > 0 ||
          (cur.phase === 'rendering' && cur.progress > 0));
        if (hasPixels) {
          deepImageVisibleRef.current = true;
          setDeep(true);
        }
      }
    }
    if (handoffRef.current) {
      handoffRef.current.operations.push(operation);
      previewPending(operation);
      syncCameraUi();
      return true;
    }
    const current = cameraRef.current;
    if (!current) return false;
    const { width: renderWidth, height: renderHeight } = propsRef.current;
    try {
      const next = applyOperation(current, operation, renderWidth, renderHeight, deepIterations);
      if (!runtimeRef.current && next.needsDeep(renderWidth, renderHeight, false)) {
        handoffRef.current = { snapshot: current.snapshot(), operations: [operation] };
        suspend({ restoreSource: false });
        previewPending(operation);
        syncCameraUi();
        startHandoff();
      } else {
        // A prefetched runtime must promote the camera before the first
        // operation crossing the handoff boundary is calculated in Decimal.
        cameraRef.current = runtimeRef.current && next.needsDeep(renderWidth, renderHeight, false) && current.kind !== 'decimal'
          ? applyOperation(runtimeRef.current.decimalCameraFromSnapshot(current.snapshot(),
            { width: renderWidth, height: renderHeight, iterationBudget: deepIterations }), operation, renderWidth, renderHeight, deepIterations)
          : next;
        if (!runtimeRef.current && next.shouldPrefetch(renderWidth, renderHeight)) ensureRuntime().catch(() => {});
        syncCameraUi();
      }
      return true;
    } catch (error) {
      report({ phase: 'limited', message: error.message });
      return false;
    }
  }, [deepIterations, ensureRuntime, previewPending, report, startHandoff, suspend, syncCameraUi]);

  const saveHistory = useCallback(() => {
    const pending = handoffRef.current;
    const snapshot = pending ? pending.snapshot : cameraRef.current?.snapshot();
    if (!snapshot) return;
    const canvas = canvasRef.current;
    const imageIsCurrent = !deepRef.current && !pending && canvas && canCaptureHistory(cameraRef.current.toShallowRanges());
    const entry = { camera: snapshot,
      pendingOperations: pending ? pending.operations.map(operation => ({ ...operation, args: [...operation.args] })) : [],
      imageData: imageIsCurrent ? captureHistoryImage(canvas) : null,
      imageWidth: canvas?.width, imageHeight: canvas?.height,
      imagePaletteKey: imageIdentityRef.current.paletteKey, imageColorFunc: imageIdentityRef.current.colorFunc };
    historyRef.current = appendHistory(historyRef.current, entry);
    redoRef.current = [];
    syncCameraUi();
  }, [canCaptureHistory, syncCameraUi]);

  const finishZoom = useCallback((immediate = false) => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
    if (!needsRefinementRef.current && !frameOptionsRef.current) return;
    if (immediate) {
      cancelFrame();
      presentRef.current({ anchor: zoomAnchorRef.current });
    } else queuePresent({ anchor: zoomAnchorRef.current });
  }, [cancelFrame, queuePresent]);

  const undo = useCallback(({ defer = true } = {}) => {
    const entry = historyRef.current[historyRef.current.length - 1];
    if (!entry) return;
    fallbackBackdropVisibleRef.current = false;
    const currentSnapshot = handoffRef.current ? handoffRef.current.snapshot : cameraRef.current?.snapshot();
    if (currentSnapshot) {
      redoRef.current = appendHistory(redoRef.current, {
        camera: currentSnapshot,
        pendingOperations: handoffRef.current
          ? handoffRef.current.operations.map(op => ({ ...op, args: [...op.args] }))
          : [],
      });
    }
    cancelFrame();
    panRef.current = pinchRef.current = null;
    wheelHistoryRef.current = false;
    loadGenerationRef.current += 1;
    controllerRef.current?.cancel();
    historyRef.current = historyRef.current.slice(0, -1);
    const runtime = runtimeRef.current;
    let limitError = null;
    const aspect = fittedAspect(aspectPolicyRef.current, width, height);
    const targetScale = fittedSpanScale(aspectPolicyRef.current, width, height);
    const savedScale = entrySpanScale(aspectPolicyRef.current, entry);
    const scaleRatio = targetScale / savedScale;
    if (runtime) {
      const base = runtime.decimalCameraFromSnapshot(entry.camera, { width, height, iterationBudget: deepIterations });
      const replay = replayOperations(base, entry.pendingOperations || [], width, height, deepIterations);
      cameraRef.current = replay.camera;
      limitError = replay.limitError;
      handoffRef.current = null;
    } else if (entry.pendingOperations?.length || Number(entry.camera.spanY) === 0 || entry.camera.precisionDigits > 16) {
      const adjustOps = scaleRatio !== 1
        ? [{ type: 'zoom', args: [scaleRatio, 0.5, 0.5] }, { type: 'aspect', args: [aspect] }]
        : [{ type: 'aspect', args: [aspect] }];
      handoffRef.current = { snapshot: entry.camera, operations: [...(entry.pendingOperations || []),
        ...adjustOps] };
      syncCameraUi();
      startHandoff();
      return;
    } else {
      cameraRef.current = numberCameraFromSnapshot(entry.camera);
      handoffRef.current = null;
    }
    if (scaleRatio !== 1) {
      cameraRef.current = cameraRef.current.zoomAt(scaleRatio, 0.5, 0.5, width, height, deepIterations);
    }
    const next = cameraRef.current.aspect === aspect ? cameraRef.current : cameraRef.current.fitAspect(aspect, width, height, deepIterations);
    cameraRef.current = next;
    syncCameraUi();
    if (!next.needsDeep(width, height, false)) {
      deepRef.current = false;
      deepImageVisibleRef.current = false;
      setDeep(false);
      report(null);
      if (canvasRef.current) canvasRef.current.style.transform = '';
      const ranges = next.toShallowRanges();
      const imageMatches = entry.imageData && entry.imageWidth === width && entry.imageHeight === height &&
        entry.imagePaletteKey === imageIdentityRef.current.paletteKey && entry.imageColorFunc === imageIdentityRef.current.colorFunc;
      if (!imageMatches || !restoreImage(ranges, entry.imageData)) presentShallow(ranges, { defer });
    } else presentRef.current({});
    if (limitError) report({ phase: 'limited', message: limitError.message });
  }, [cancelFrame, deepIterations, height, presentShallow, report, restoreImage, startHandoff, syncCameraUi, width]);

  const redo = useCallback(() => {
    const entry = redoRef.current[redoRef.current.length - 1];
    if (!entry) return;
    fallbackBackdropVisibleRef.current = false;
    const currentSnapshot = handoffRef.current ? handoffRef.current.snapshot : cameraRef.current?.snapshot();
    if (currentSnapshot) {
      historyRef.current = appendHistory(historyRef.current, {
        camera: currentSnapshot,
        pendingOperations: handoffRef.current
          ? handoffRef.current.operations.map(op => ({ ...op, args: [...op.args] }))
          : [],
      });
    }
    cancelFrame();
    panRef.current = pinchRef.current = null;
    wheelHistoryRef.current = false;
    loadGenerationRef.current += 1;
    controllerRef.current?.cancel();
    redoRef.current = redoRef.current.slice(0, -1);
    const runtime = runtimeRef.current;
    const aspect = fittedAspect(aspectPolicyRef.current, width, height);
    const targetScale = fittedSpanScale(aspectPolicyRef.current, width, height);
    const savedScale = entrySpanScale(aspectPolicyRef.current, entry);
    const scaleRatio = targetScale / savedScale;
    if (runtime) {
      const base = runtime.decimalCameraFromSnapshot(entry.camera, { width, height, iterationBudget: deepIterations });
      const replay = replayOperations(base, entry.pendingOperations || [], width, height, deepIterations);
      cameraRef.current = replay.camera;
      handoffRef.current = null;
    } else if (entry.pendingOperations?.length || Number(entry.camera.spanY) === 0 || entry.camera.precisionDigits > 16) {
      const adjustOps = scaleRatio !== 1
        ? [{ type: 'zoom', args: [scaleRatio, 0.5, 0.5] }, { type: 'aspect', args: [aspect] }]
        : [{ type: 'aspect', args: [aspect] }];
      handoffRef.current = {
        snapshot: entry.camera,
        operations: [...(entry.pendingOperations || []), ...adjustOps],
      };
      syncCameraUi();
      startHandoff();
      return;
    } else {
      cameraRef.current = numberCameraFromSnapshot(entry.camera);
      handoffRef.current = null;
    }
    if (scaleRatio !== 1) {
      cameraRef.current = cameraRef.current.zoomAt(scaleRatio, 0.5, 0.5, width, height, deepIterations);
    }
    cameraRef.current = cameraRef.current.aspect === aspect
      ? cameraRef.current
      : cameraRef.current.fitAspect(aspect, width, height, deepIterations);
    syncCameraUi();
    if (!cameraRef.current.needsDeep(width, height, false)) {
      deepRef.current = false;
      deepImageVisibleRef.current = false;
      setDeep(false);
      report(null);
      if (canvasRef.current) canvasRef.current.style.transform = '';
      presentShallow(cameraRef.current.toShallowRanges(), { defer: true });
    } else {
      presentRef.current({});
    }
  }, [cancelFrame, deepIterations, height, presentShallow, report, startHandoff, syncCameraUi, width]);

  const resetView = useCallback(({ clearHistory = false } = {}) => {
    const baseCamera = initialSnapshot ? null : cameraFromRanges(initialX, initialY);
    const aspect = fittedAspect(aspectPolicyRef.current, width, height);
    const spanScale = fittedSpanScale(aspectPolicyRef.current, width, height);
    const scaledBase = baseCamera && spanScale !== 1
      ? baseCamera.zoomAt(spanScale, 0.5, 0.5, width, height, deepIterations)
      : baseCamera;
    const targetInitial = baseCamera
      ? (scaledBase.aspect === aspect ? scaledBase : scaledBase.fitAspect(aspect, width, height, deepIterations))
      : null;
    const currentSnap = handoffRef.current ? handoffRef.current.snapshot : cameraRef.current?.snapshot();
    const targetSnap = initialSnapshot
      ? (spanScale === 1 ? { ...initialSnapshot, aspect } : null)
      : targetInitial?.snapshot();
    if (currentSnap && targetSnap && !handoffRef.current?.operations?.length &&
        ['centerX', 'centerY', 'spanY', 'aspect'].every(k => currentSnap[k] === targetSnap[k])) {
      if (clearHistory && historyRef.current.length > 0) {
        historyRef.current = [];
        redoRef.current = [];
        syncCameraUi();
      }
      return false;
    }
    finishZoom(true);
    if (clearHistory) {
      historyRef.current = [];
      redoRef.current = [];
    } else saveHistory();
    cancelFrame();
    panRef.current = pinchRef.current = null;
    wheelHistoryRef.current = false;
    loadGenerationRef.current += 1;
    controllerRef.current?.cancel();
    if (initialSnapshot) {
      fallbackBackdropVisibleRef.current = Boolean(fallbackSrc && !isFullscreenRef.current);
      deepImageVisibleRef.current = false;
      setDeep(false);
      const runtime = runtimeRef.current;
      if (runtime) {
        const base = runtime.decimalCameraFromSnapshot(initialSnapshot, { width, height, iterationBudget: deepIterations });
        const scaled = spanScale !== 1 ? base.zoomAt(spanScale, 0.5, 0.5, width, height, deepIterations) : base;
        cameraRef.current = scaled.aspect === aspect ? scaled : scaled.fitAspect(aspect, width, height, deepIterations);
        handoffRef.current = null;
        syncCameraUi();
        presentRef.current({});
      } else {
        const ops = [];
        if (spanScale !== 1) ops.push({ type: 'zoom', args: [spanScale, 0.5, 0.5] });
        if (aspect !== initialSnapshot.aspect) ops.push({ type: 'aspect', args: [aspect] });
        handoffRef.current = {
          snapshot: initialSnapshot,
          operations: ops,
        };
        syncCameraUi();
        startHandoff();
      }
      return true;
    }
    handoffRef.current = null;
    cameraRef.current = targetInitial;
    deepRef.current = false;
    deepImageVisibleRef.current = false;
    shallowPreviewVisibleRef.current = false;
    fallbackBackdropVisibleRef.current = false;
    setDeep(false);
    report(null);
    if (canvasRef.current) canvasRef.current.style.transform = '';
    if (deepCanvasRef.current) deepCanvasRef.current.style.transform = '';
    if (fallbackImgRef.current && !isFullscreenRef.current) fallbackImgRef.current.style.visibility = '';
    syncCameraUi();
    presentShallow(targetInitial.toShallowRanges(), {});
    return true;
  }, [cancelFrame, deepIterations, fallbackSrc, finishZoom, height, initialSnapshot, initialX, initialY, isFullscreenRef, presentShallow, report, saveHistory, startHandoff, syncCameraUi, width]);

  const dimensions = useCallback(() => {
    const canvas = canvasRef.current;
    const canvasRect = isFullscreenRef.current ? canvas?.getBoundingClientRect?.() : null;
    const rect = canvasRect?.width > 0 && canvasRect?.height > 0
      ? canvasRect : canvas.parentElement.getBoundingClientRect();
    const displayWidth = (isFullscreenRef.current && rect.width) || canvas.offsetWidth || width;
    const displayHeight = (isFullscreenRef.current && rect.height) || canvas.offsetHeight || height;
    // The viewport preview keeps its old aspect and centers the bitmap while
    // the section changes shape. Map gestures through that visible bitmap.
    return { left: rect.left + ((rect.width ?? displayWidth) - displayWidth) / 2,
      top: rect.top, width: displayWidth, height: displayHeight };
  }, [height, isFullscreenRef, width]);

  const startPan = useCallback((x, y) => {
    finishZoom(true);
    wheelHistoryRef.current = false;
    panRef.current = { x, y, moved: false };
  }, [finishZoom]);

  const movePan = useCallback((x, y) => {
    const start = panRef.current;
    if (!start || (x === start.x && y === start.y)) return;
    if (!start.moved) saveHistory();
    start.moved = true;
    const size = dimensions();
    const operation = { type: 'pan', args: [start.x - x, start.y - y, size.width, size.height] };
    start.x = x;
    start.y = y;
    if (operate(operation)) queuePresent({ interactive: true, pan: true });
  }, [dimensions, operate, queuePresent, saveHistory]);

  const finishPan = useCallback(() => {
    const start = panRef.current;
    panRef.current = null;
    if (!start?.moved) return;
    cancelFrame();
    presentRef.current({ pan: true });
  }, [cancelFrame]);

  useEffect(() => {
    mountedRef.current = true;
    if (handoffRef.current) {
      suspend({ restoreSource: false });
      startHandoff();
    } else if (cameraRef.current?.needsDeep(width, height, false)) {
      suspend({ restoreSource: false });
      handoffRef.current = { snapshot: cameraRef.current.snapshot(), operations: [] };
      startHandoff();
    } else if (cameraRef.current?.shouldPrefetch(width, height)) {
      ensureRuntime().catch(() => {});
    }
    return () => {
      mountedRef.current = false;
      if (prewarmRef.current?.idle != null) window.cancelIdleCallback?.(prewarmRef.current.idle);
      if (prewarmRef.current?.timer != null) clearTimeout(prewarmRef.current.timer);
      prewarmRef.current = null;
      loadGenerationRef.current += 1;
      cancelFrame();
      controllerRef.current?.dispose();
      controllerRef.current = null;
      controllerCanvasRef.current = null;
    };
    // A mount owns one camera; prop replacement is handled independently below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const presetKey = JSON.stringify(initialSnapshot || [initialX, initialY]);
  const previousPresetRef = useRef(presetKey);
  useEffect(() => {
    if (previousPresetRef.current === presetKey) return;
    previousPresetRef.current = presetKey;
    cancelFrame();
    loadGenerationRef.current += 1;
    controllerRef.current?.cancel();
    historyRef.current = [];
    aspectPolicyRef.current = { aspect: initialSnapshot?.aspect || cameraFromRanges(initialX, initialY).aspect, width: propWidth, height: propHeight };
    spanScaleBaselineRef.current = null;
    fallbackBackdropVisibleRef.current = Boolean(fallbackSrc && initialSnapshot);
    if (initialSnapshot) {
      handoffRef.current = { snapshot: initialSnapshot, operations: [] };
      syncCameraUi();
      startHandoff();
    } else {
      handoffRef.current = null;
      cameraRef.current = runtimeRef.current
        ? runtimeRef.current.decimalCameraFromSnapshot(cameraFromRanges(initialX, initialY).snapshot(),
          { ...propsRef.current, iterationBudget: deepIterations })
        : cameraFromRanges(initialX, initialY);
      syncCameraUi();
      presentRef.current({});
    }
  }, [cancelFrame, deepIterations, fallbackSrc, initialSnapshot, initialX, initialY, presetKey, propHeight, propWidth, startHandoff, syncCameraUi]);

  useLayoutEffect(() => {
    if (!isFullscreen) {
      setFullscreenSize(null);
      return undefined;
    }
    const updateFullscreenSize = () => {
      const next = measureFullscreenDimensions(canvasRef.current, containerRef.current, propWidth, propHeight);
      setFullscreenSize(prev => (prev?.width === next.width && prev?.height === next.height ? prev : next));
    };
    updateFullscreenSize();
    window.addEventListener('resize', updateFullscreenSize);
    return () => window.removeEventListener('resize', updateFullscreenSize);
  }, [containerRef, isFullscreen, propHeight, propWidth]);

  const previousSizeRef = useRef({ width, height });
  useLayoutEffect(() => {
    const resized = previousSizeRef.current.width !== width || previousSizeRef.current.height !== height;
    const prevSize = previousSizeRef.current;
    previousSizeRef.current = { width, height };
    if (resized) {
      const prevScale = fittedSpanScale(aspectPolicyRef.current, prevSize.width, prevSize.height);
      const nextScale = fittedSpanScale(aspectPolicyRef.current, width, height);
      const nextAspect = fittedAspect(aspectPolicyRef.current, width, height);
      if (nextScale !== prevScale) {
        const currentSnap = !handoffRef.current && cameraRef.current?.snapshot();
        if (prevScale === 1 && currentSnap) {
          spanScaleBaselineRef.current = { baseSpanY: currentSnap.spanY, scaledSpanY: null };
        }
        if (nextScale === 1 && spanScaleBaselineRef.current && currentSnap &&
            currentSnap.spanY === spanScaleBaselineRef.current.scaledSpanY) {
          const restoredSnap = { ...currentSnap, spanY: spanScaleBaselineRef.current.baseSpanY, aspect: nextAspect };
          cameraRef.current = cameraRef.current.kind === 'decimal' && runtimeRef.current
            ? runtimeRef.current.decimalCameraFromSnapshot(restoredSnap, { width, height, iterationBudget: deepIterations })
            : numberCameraFromSnapshot(restoredSnap);
          spanScaleBaselineRef.current = null;
          syncCameraUi();
        } else {
          operate({ type: 'zoom', args: [nextScale / prevScale, 0.5, 0.5] });
          operate({ type: 'aspect', args: [nextAspect] });
          if (nextScale === 1) spanScaleBaselineRef.current = null;
          else if (spanScaleBaselineRef.current && !handoffRef.current && cameraRef.current) {
            spanScaleBaselineRef.current.scaledSpanY = cameraRef.current.snapshot().spanY;
          }
        }
      } else {
        operate({ type: 'aspect', args: [nextAspect] });
      }
    }
    if ((deepRef.current || resized) && cameraRef.current && !handoffRef.current) {
      presentRef.current(resized ? { presentationTime: performance.now() } : {});
    }
  }, [colorParams, deepIterations, width, height, operate, syncCameraUi]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const keyDown = event => {
      if (event.key === 'Shift') setShiftPressed(true);
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target?.tagName)) return;
      const activeCanvas = isFullscreenRef.current ||
        (typeof document !== 'undefined' && document.activeElement === canvas.parentElement) ||
        canvas.parentElement.matches(':hover');
      if (!activeCanvas) return;
      if ((event.metaKey || event.ctrlKey) && !event.shiftKey &&
          (event.key === 'z' || event.key === 'Z')) {
        event.preventDefault();
        undo();
        return;
      }
      if ((event.metaKey || event.ctrlKey) &&
          ((event.shiftKey && (event.key === 'z' || event.key === 'Z')) ||
           (!event.shiftKey && (event.key === 'y' || event.key === 'Y')))) {
        event.preventDefault();
        redo();
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key === '+' || event.key === '=') {
        event.preventDefault();
        finishZoom(true);
        saveHistory();
        if (operate({ type: 'zoom', args: [1 / 1.2, 0.5, 0.5] })) presentRef.current({});
      } else if (event.key === '-' || event.key === '_') {
        event.preventDefault();
        finishZoom(true);
        saveHistory();
        if (operate({ type: 'zoom', args: [1.2, 0.5, 0.5] })) presentRef.current({});
      } else if (event.key === '0') {
        event.preventDefault();
        resetView();
      } else if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
        event.preventDefault();
        finishZoom(true);
        saveHistory();
        const size = dimensions();
        const stepX = event.key === 'ArrowLeft' ? -size.width * 0.1 : event.key === 'ArrowRight' ? size.width * 0.1 : 0;
        const stepY = event.key === 'ArrowUp' ? -size.height * 0.1 : event.key === 'ArrowDown' ? size.height * 0.1 : 0;
        if (operate({ type: 'pan', args: [stepX, stepY, size.width, size.height] })) {
          presentRef.current({ pan: true });
        }
      }
    };
    const keyUp = event => { if (event.key === 'Shift') setShiftPressed(false); };
    const blur = () => { setShiftPressed(false); finishPan(); finishZoom(); };
    window.addEventListener('keydown', keyDown);
    window.addEventListener('keyup', keyUp);
    window.addEventListener('blur', blur);
    return () => {
      window.removeEventListener('keydown', keyDown);
      window.removeEventListener('keyup', keyUp);
      window.removeEventListener('blur', blur);
    };
  }, [canvasEpoch, dimensions, finishPan, finishZoom, isFullscreenRef, operate, redo, resetView, saveHistory, undo]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const surface = canvas.parentElement;
    const wheel = event => {
      if (!event.shiftKey && !event.ctrlKey) return;
      event.preventDefault();
      event.stopPropagation();
      if (!wheelHistoryRef.current) { saveHistory(); wheelHistoryRef.current = true; }
      const size = dimensions();
      const delta = Math.abs(event.deltaY) > Math.abs(event.deltaX) ? event.deltaY : event.deltaX;
      // Browsers send trackpad pinches as ctrl+wheel. Restore their faster
      // response while keeping full mouse-wheel notches modest.
      const unit = event.deltaMode === 1 ? 40 : event.deltaMode === 2 ? 400 : 1;
      const rate = event.ctrlKey ? TRACKPAD_PINCH_ZOOM_RATE : WHEEL_ZOOM_RATE;
      const maxStep = event.ctrlKey ? MAX_TRACKPAD_PINCH_ZOOM_STEP : MAX_WHEEL_ZOOM_STEP;
      const amount = Math.max(-maxStep, Math.min(maxStep,
        (event.ctrlKey ? 1 : -1) * delta * unit * rate));
      const u = (event.clientX - size.left) / size.width;
      const v = (event.clientY - size.top) / size.height;
      if (operate({ type: 'zoom', args: [Math.exp(amount), u, v] })) {
        queuePresent({ interactive: true, anchor: [u, v] });
      }
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => { wheelHistoryRef.current = false; finishZoom(); }, 150);
    };
    const pinchPosition = touches => {
      const size = dimensions();
      const x = (touches[0].clientX + touches[1].clientX) / 2;
      const y = (touches[0].clientY + touches[1].clientY) / 2;
      return { x, y, u: (x - size.left) / size.width, v: (y - size.top) / size.height,
        distance: Math.hypot(touches[1].clientX - touches[0].clientX, touches[1].clientY - touches[0].clientY) || 1 };
    };
    const finishTouch = () => {
      const gesture = pinchRef.current;
      if (!gesture) return;
      if (gesture.moved && !gesture.zoomed) {
        cancelFrame();
        presentRef.current({ pan: true, anchor: [gesture.u, gesture.v] });
      } else finishZoom();
      pinchRef.current = null;
      wheelHistoryRef.current = false;
    };
    const startTouch = event => {
      if (event.touches.length !== 2 || !event.cancelable) {
        finishTouch();
        return;
      }
      // One finger belongs to page scrolling. Only claim a two-finger gesture.
      event.preventDefault();
      finishPan();
      pinchRef.current = { ...pinchPosition(event.touches), moved: false, zoomed: false };
    };
    const moveTouch = event => {
      if (event.touches.length === 2 && pinchRef.current && event.cancelable) {
        event.preventDefault();
        const previous = pinchRef.current;
        const next = pinchPosition(event.touches);
        const factor = Math.max(0.25, Math.min(4, previous.distance / next.distance));
        const panning = next.x !== previous.x || next.y !== previous.y;
        if (factor === 1 && !panning) return;
        if (!previous.moved && !wheelHistoryRef.current) { saveHistory(); wheelHistoryRef.current = true; }
        if (factor !== 1) operate({ type: 'zoom', args: [factor, previous.u, previous.v] });
        // Translation of the pinch midpoint keeps its original world anchor
        // under the fingers, including a changing midpoint while importing.
        if (panning) {
          const size = dimensions();
          operate({ type: 'pan', args: [previous.x - next.x, previous.y - next.y, size.width, size.height] });
        }
        const zoomed = previous.zoomed || factor !== 1;
        pinchRef.current = { ...next, moved: true, zoomed };
        queuePresent({ interactive: true, pan: !zoomed, anchor: [next.u, next.v] });
      } else finishTouch();
    };
    const endTouch = event => {
      if (event.touches.length !== 2 || event.type === 'touchcancel') finishTouch();
    };
    const options = { passive: false };
    surface.addEventListener('wheel', wheel, options);
    surface.addEventListener('touchstart', startTouch, options);
    surface.addEventListener('touchmove', moveTouch, options);
    surface.addEventListener('touchend', endTouch, options);
    surface.addEventListener('touchcancel', endTouch, options);
    return () => {
      surface.removeEventListener('wheel', wheel);
      surface.removeEventListener('touchstart', startTouch);
      surface.removeEventListener('touchmove', moveTouch);
      surface.removeEventListener('touchend', endTouch);
      surface.removeEventListener('touchcancel', endTouch);
    };
  }, [canvasEpoch, cancelFrame, dimensions, finishPan, finishZoom, operate, queuePresent, saveHistory]);

  useEffect(() => {
    if (typeof window === 'undefined' || !new URLSearchParams(window.location.search).has('deepZoomTest')) return;
    const canvas = canvasRef.current;
    Object.defineProperty(canvas, '__fractalCamera', { configurable: true, get: () => cameraRef.current?.snapshot() || handoffRef.current?.snapshot });
    Object.defineProperty(canvas, '__fractalDeepStatus', { configurable: true, get: () => statusRef.current });
    Object.defineProperty(canvas, '__fractalController', { configurable: true, get: () => controllerRef.current });
    canvas.__setFractalCamera = snapshot => {
      cancelFrame();
      controllerRef.current?.cancel();
      fallbackBackdropVisibleRef.current = false;
      if (fallbackImgRef.current) fallbackImgRef.current.style.visibility = 'hidden';
      suspend({ restoreSource: false });
      if (Number.isFinite(snapshot?.aspect) && snapshot.aspect > 0) {
        aspectPolicyRef.current = {
          aspect: snapshot.aspect,
          width: propsRef.current.width,
          height: propsRef.current.height,
        };
      }
      spanScaleBaselineRef.current = null;
      handoffRef.current = { snapshot, operations: [] };
      syncCameraUi();
      startHandoff();
    };
    return () => {
      delete canvas.__fractalCamera;
      delete canvas.__fractalDeepStatus;
      delete canvas.__fractalController;
      delete canvas.__setFractalCamera;
    };
  }, [canvasEpoch, cancelFrame, startHandoff, suspend, syncCameraUi]);

  const retry = () => {
    if (handoffRef.current) startHandoff(true);
    else if (controllerRef.current) controllerRef.current.retry();
    else presentRef.current({});
  };
  const phase = status?.phase;
  const statusText = deepStatusText(status, width, height);
  const hasControls = Boolean(showControls || initialSnapshot || deep || isFullscreen);
  const isDeepActive = Boolean(deep || handoffRef.current || status || initialSnapshot);
  const activeIterations = isDeepActive
    ? (status?.iterationBudget || status?.progress?.iterationBudget || deepIterations)
    : SHALLOW_MAX_ITERATIONS;
  const viewDescription = isFullscreen ? viewText.fullscreen : viewText.inline;
  // A backing-size change clears a canvas immediately. Keep the old shallow
  // bitmap through a pending deep handoff, then resize
  // underneath the visible deep image. A handoff that ends in shallow mode
  // redraws after the new backing dimensions reach the DOM.
  const freezeShallowBacking = !!getPresentedSnapshot() &&
    (!!handoffRef.current ||
      deepRef.current && !deepImageVisibleRef.current);
  if (freezeShallowBacking) backingFrozenRef.current = true;
  else {
    if (backingFrozenRef.current && !deepRef.current &&
        (shallowBackingRef.current.width !== width || shallowBackingRef.current.height !== height)) {
      refreshShallowAfterUnfreezeRef.current = true;
    }
    shallowBackingRef.current = { width, height };
    backingFrozenRef.current = false;
  }
  useLayoutEffect(() => {
    if (!refreshShallowAfterUnfreezeRef.current) return;
    refreshShallowAfterUnfreezeRef.current = false;
    if (mountedRef.current && !handoffRef.current && !deepRef.current) presentRef.current({});
  });
  return <figure>
    <div ref={containerRef}
        className={`image-container${hasControls ? ' deep-zoom-container' : ''}${isFullscreen ? ' fullscreen' : ''}`}
        data-fullscreen={isFullscreen ? 'true' : 'false'}
        tabIndex={0}
        style={{ position: 'relative', overflow: 'hidden', cursor: shiftPressed ? 'crosshair' : 'move', touchAction: 'pan-y' }}
        onDoubleClick={event => {
          finishZoom(true);
          saveHistory();
          const size = dimensions();
          const factor = event.shiftKey ? 1.2 : 1 / 1.2;
          if (operate({ type: 'zoom', args: [factor, (event.clientX - size.left) / size.width, (event.clientY - size.top) / size.height] })) presentRef.current({});
        }}
        onMouseDown={event => {
          if (event.button !== 0) return;
          if (event.shiftKey) {
            finishZoom(true);
            const size = dimensions();
            const startX = Math.max(0, Math.min(size.width, event.clientX - size.left));
            const startY = Math.max(0, Math.min(size.height, event.clientY - size.top));
            boxSelectRef.current = {
              startX,
              startY,
              currentX: startX,
              currentY: startY,
              width: size.width,
              height: size.height,
            };
            setBoxSelect(null);
            return;
          }
          startPan(event.pageX, event.pageY);
        }}
        onMouseMove={event => {
          if (boxSelectRef.current) {
            const size = dimensions();
            const currentX = Math.max(0, Math.min(size.width, event.clientX - size.left));
            const currentY = Math.max(0, Math.min(size.height, event.clientY - size.top));
            boxSelectRef.current.currentX = currentX;
            boxSelectRef.current.currentY = currentY;
            boxSelectRef.current.width = size.width;
            boxSelectRef.current.height = size.height;
            const dx = Math.abs(currentX - boxSelectRef.current.startX);
            const dy = Math.abs(currentY - boxSelectRef.current.startY);
            if (dx >= 4 || dy >= 4) {
              setBoxSelect({
                left: (Math.min(boxSelectRef.current.startX, currentX) / size.width) * 100,
                top: (Math.min(boxSelectRef.current.startY, currentY) / size.height) * 100,
                width: (dx / size.width) * 100,
                height: (dy / size.height) * 100,
              });
            }
            return;
          }
          movePan(event.pageX, event.pageY);
        }}
        onMouseUp={event => {
          if (boxSelectRef.current) {
            const size = dimensions();
            const endX = Math.max(0, Math.min(size.width, event.clientX - size.left));
            const endY = Math.max(0, Math.min(size.height, event.clientY - size.top));
            const box = boxSelectRef.current;
            boxSelectRef.current = null;
            setBoxSelect(null);
            const dx = Math.abs(endX - box.startX);
            const dy = Math.abs(endY - box.startY);
            if (dx >= 8 && dy >= 8 && size.width > 0 && size.height > 0) {
              const u = (box.startX + endX) / (2 * size.width);
              const v = (box.startY + endY) / (2 * size.height);
              const factor = Math.max(0.02, Math.min(0.95, Math.max(dx / size.width, dy / size.height)));
              saveHistory();
              if (operate({ type: 'zoom', args: [factor, u, v] })) {
                presentRef.current({ anchor: [u, v] });
              }
            }
            return;
          }
          if (panRef.current) movePan(event.pageX, event.pageY);
          finishPan();
        }}
        onMouseLeave={() => {
          if (boxSelectRef.current) {
            boxSelectRef.current = null;
            setBoxSelect(null);
          }
          finishPan();
        }}
      >
      {fallbackSrc && <img
        ref={fallbackImgRef}
        loading={resolvedFetchPriority === 'high' ? undefined : 'lazy'}
        fetchPriority={resolvedFetchPriority}
        decoding="async"
        src={fallbackSrc}
        alt={fallbackAlt || 'Julia set fractal'}
        width={imgWidth}
        height={imgHeight}
        style={hideFallbackImg ? { visibility: 'hidden' } : undefined}
      />}
      <canvas key={canvasEpoch} ref={canvasRef}
        width={shallowBackingRef.current.width} height={shallowBackingRef.current.height}
        style={{ display: 'block', pointerEvents: 'none', ...(isFullscreen ? { width: '100%', height: '100%' } : {}) }} />
      <canvas ref={deepCanvasRef} width={width} height={height} aria-hidden="true"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none',
          visibility: deep ? 'visible' : 'hidden', opacity: deep ? 1 : 0 }} />
      {boxSelect && (
        <div
          className="box-zoom-marquee"
          data-testid="box-zoom-marquee"
          aria-hidden="true"
          style={{
            left: `${boxSelect.left}%`,
            top: `${boxSelect.top}%`,
            width: `${boxSelect.width}%`,
            height: `${boxSelect.height}%`,
          }}
        />
      )}
      {recovering && !deep && <div className="canvas-recovery" role={hasControls || statusText ? undefined : 'status'}>Restoring view…</div>}
      {hasControls && <div className="deep-zoom-controls" data-nosnippet="">
        <button type="button" onClick={() => undo({ defer: false })} disabled={historyLength === 0}>Undo</button>
        <button type="button" onClick={() => resetView({ clearHistory: true })}>Reset</button>
      </div>}
      {hasControls && <div className="deep-zoom-status" data-nosnippet="">
        <span>{viewDescription}</span>
        <span role="status">
          {`${activeIterations.toLocaleString()} iter max · ${statusText || (recovering && !deep ? 'Restoring view…' : 'Ready')}`}
          {(phase === 'error' || phase === 'limited') && <> <button type="button" onClick={retry}>Retry</button></>}
        </span>
      </div>}
      {!hasControls && statusText && <div className="deep-zoom-status" role="status" data-nosnippet="">
        {statusText}
        {(phase === 'error' || phase === 'limited') && <> <button type="button" onClick={retry}>Retry</button></>}
      </div>}
      {showFullscreenButton && !hasControls && (
        <div
          className="canvas-overlay-toolbar"
          onMouseDown={event => event.stopPropagation()}
          onDoubleClick={event => event.stopPropagation()}
        >
          <button
            type="button"
            className="canvas-action-button"
            aria-label="Reset view"
            title="Reset view (0)"
            onClick={() => resetView()}
          >
            &#x21BA;
          </button>
        </div>
      )}
      {showFullscreenButton && <FullscreenButton isFullscreen={isFullscreen} onToggle={toggleFullscreen} />}
    </div>
  </figure>;
}
